import type { RenderContextKind } from '../../elements/types';
import { normalizeBindingArgForPolicy } from './bindingArgPolicy';
import { createBindingId } from './bindingId';
import { RUNTIME_DATA_SERVICE_CLASSES } from './serviceDefinitions';
import type {
    ActiveDataBinding,
    BindingId,
    RuntimeDataServiceClass,
    RuntimeResultLinkDescriptor,
} from './types';
import { assertRuntimeBindingCompatibilityMetadata } from './bindingCompatibility';

export interface RuntimeBindingDescriptor<
    TLink extends RuntimeResultLinkDescriptor = RuntimeResultLinkDescriptor
> {
    readonly bindingId: BindingId;
    readonly binding: ActiveDataBinding;
    readonly link: TLink;
}

export { createBindingId } from './bindingId';

function collectBindingDescriptors(
    serviceDefinitions: readonly RuntimeDataServiceClass[]
): readonly RuntimeBindingDescriptor[] {
    const descriptors: RuntimeBindingDescriptor[] = [];
    const byBindingId = new Map<BindingId, RuntimeBindingDescriptor>();

    serviceDefinitions.forEach((serviceDefinition) => {
        serviceDefinition.serviceResultLinks.forEach((resultLink) => {
            const binding: ActiveDataBinding = {
                serviceId: serviceDefinition.serviceId,
                linkKey: resultLink.key,
            };
            const bindingId = createBindingId(binding);
            assertRuntimeBindingCompatibilityMetadata(bindingId, resultLink);
            const descriptor: RuntimeBindingDescriptor = Object.freeze({
                bindingId,
                binding,
                link: resultLink,
            });

            if (byBindingId.has(bindingId)) {
                throw new Error(
                    `Duplicate runtime binding descriptor "${bindingId}". Binding IDs must be globally unique.`
                );
            }
            byBindingId.set(bindingId, descriptor);
            descriptors.push(descriptor);
        });
    });

    return Object.freeze(descriptors);
}

function filterByContext(
    descriptors: readonly RuntimeBindingDescriptor[],
    contextKind: RenderContextKind
): RuntimeBindingDescriptor[] {
    return descriptors.filter((descriptor) =>
        descriptor.link.supportedContextKinds.includes(contextKind)
    );
}

function filterByValueKind<TValueKind extends RuntimeResultLinkDescriptor['valueKind']>(
    descriptors: readonly RuntimeBindingDescriptor[],
    valueKind: TValueKind
): Array<
    RuntimeBindingDescriptor<Extract<RuntimeResultLinkDescriptor, { valueKind: TValueKind }>>
> {
    return descriptors.filter((descriptor) => descriptor.link.valueKind === valueKind) as Array<
        RuntimeBindingDescriptor<Extract<RuntimeResultLinkDescriptor, { valueKind: TValueKind }>>
    >;
}

export class BindingCatalog {
    private readonly descriptors: readonly RuntimeBindingDescriptor[];

    private readonly descriptorsByBindingId: ReadonlyMap<BindingId, RuntimeBindingDescriptor>;

    private constructor(serviceDefinitions: readonly RuntimeDataServiceClass[]) {
        this.descriptors = collectBindingDescriptors(serviceDefinitions);
        this.descriptorsByBindingId = new Map(
            this.descriptors.map((descriptor) => [descriptor.bindingId, descriptor])
        );
    }

    static fromServiceDefinitions(
        serviceDefinitions: readonly RuntimeDataServiceClass[] = RUNTIME_DATA_SERVICE_CLASSES
    ): BindingCatalog {
        return new BindingCatalog(serviceDefinitions);
    }

    listAll(): readonly RuntimeBindingDescriptor[] {
        return this.descriptors;
    }

    listForContext(contextKind: RenderContextKind): readonly RuntimeBindingDescriptor[] {
        return filterByContext(this.descriptors, contextKind);
    }

    listForValueKind<TValueKind extends RuntimeResultLinkDescriptor['valueKind']>(
        valueKind: TValueKind
    ): readonly RuntimeBindingDescriptor<
        Extract<RuntimeResultLinkDescriptor, { valueKind: TValueKind }>
    >[] {
        return filterByValueKind(this.descriptors, valueKind);
    }

    listForContextAndValueKind<TValueKind extends RuntimeResultLinkDescriptor['valueKind']>(
        contextKind: RenderContextKind,
        valueKind: TValueKind
    ): readonly RuntimeBindingDescriptor<
        Extract<RuntimeResultLinkDescriptor, { valueKind: TValueKind }>
    >[] {
        return filterByValueKind(this.listForContext(contextKind), valueKind);
    }

    getByIdOrThrow(bindingId: BindingId): RuntimeBindingDescriptor {
        const descriptor = this.descriptorsByBindingId.get(bindingId);
        if (descriptor) {
            return descriptor;
        }

        throw new Error(`Unsupported runtime binding "${bindingId}".`);
    }

    getByIdAndValueKindOrThrow<TValueKind extends RuntimeResultLinkDescriptor['valueKind']>(
        bindingId: BindingId,
        valueKind: TValueKind
    ): RuntimeBindingDescriptor<Extract<RuntimeResultLinkDescriptor, { valueKind: TValueKind }>> {
        const descriptor = this.getByIdOrThrow(bindingId);
        if (descriptor.link.valueKind === valueKind) {
            return descriptor as RuntimeBindingDescriptor<
                Extract<RuntimeResultLinkDescriptor, { valueKind: TValueKind }>
            >;
        }

        throw new Error(`Runtime binding "${bindingId}" does not resolve to "${valueKind}".`);
    }

    resolveActiveBindingByIdAndValueKindOrThrow<
        TValueKind extends RuntimeResultLinkDescriptor['valueKind']
    >(bindingId: BindingId, valueKind: TValueKind, arg?: string | null): ActiveDataBinding {
        const descriptor = this.getByIdAndValueKindOrThrow(bindingId, valueKind);
        return this.toActiveDataBinding(descriptor, arg);
    }

    toActiveDataBinding(
        descriptorOrBindingId: RuntimeBindingDescriptor | BindingId,
        arg?: string | null
    ): ActiveDataBinding {
        const descriptor =
            typeof descriptorOrBindingId === 'string'
                ? this.getByIdOrThrow(descriptorOrBindingId)
                : descriptorOrBindingId;
        const { binding } = descriptor;
        const bindingId = createBindingId(binding);
        const normalizedArg = normalizeBindingArgForPolicy(
            arg,
            descriptor.link.argPolicy,
            bindingId,
            descriptor.link.argOptions
        );

        return {
            serviceId: binding.serviceId,
            linkKey: binding.linkKey,
            ...(normalizedArg === null ? {} : { arg: normalizedArg }),
        };
    }

    toPresetKey(bindingId: BindingId, prefix?: string): string {
        const normalizedBindingId = bindingId.replaceAll('.', '_');
        if (!prefix) {
            return normalizedBindingId;
        }

        return `${prefix}_${normalizedBindingId}`;
    }
}

let bindingCatalogCache: BindingCatalog | null = null;

export function getBindingCatalog(): BindingCatalog {
    if (bindingCatalogCache === null) {
        bindingCatalogCache = BindingCatalog.fromServiceDefinitions();
    }

    return bindingCatalogCache;
}

export function invalidateBindingCatalog(): void {
    bindingCatalogCache = null;
}
